import type {
  Entry,
  Client,
  Config,
  LoadAllResponse,
  LoginResponse,
} from "@ascente/shared-types";

const SCRIPT_URL = import.meta.env.VITE_SCRIPT_URL as string;

/**
 * Shared secret the backend checks before it touches the spreadsheet.
 *
 * Added centrally in `post` and `get` so no action can forget it. Like
 * VITE_SCRIPT_URL this is inlined into the bundle and is therefore readable
 * by anyone who views source — it stops an anonymous caller who merely knows
 * the URL, not a determined one. See docs/SECURITY.md.
 */
const API_SECRET = import.meta.env.VITE_API_SECRET as string;

async function post<T>(body: Record<string, unknown>): Promise<T> {
  const res = await fetch(SCRIPT_URL, {
    method: "POST",
    // Apps Script rejects a preflight, so the body must be sent as
    // text/plain. This matches fetch's default for a string body; it is
    // written out because the backend depends on it.
    headers: { "Content-Type": "text/plain" },
    body: JSON.stringify({ ...body, secret: API_SECRET }),
    // Apps Script answers a POST with a redirect to the script output.
    redirect: "follow",
  });
  return res.json() as Promise<T>;
}

/**
 * What every POST action answers with.
 *
 * `doPost` on the backend wraps its whole switch in a try/catch that
 * returns `{ success: false, error }` on any throw, and answers an
 * unrecognised action the same way. So a failure — and an explanation of
 * it — can come back from any action, not only from the handlers that
 * return one explicitly. Callers must read `success`; an HTTP 200 means
 * nothing on its own.
 */
export interface MutationResponse {
  success: boolean;
  error?: string | undefined;
}

async function get<T>(params: Record<string, string>): Promise<T> {
  const url = new URL(SCRIPT_URL);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  url.searchParams.set("secret", API_SECRET);
  const res = await fetch(url.toString());
  return res.json() as Promise<T>;
}

export const api = {
  login: (username: string, password: string) =>
    post<LoginResponse>({ action: "login", username, password }),

  init: () => post<MutationResponse>({ action: "init" }),

  loadAll: (userName: string) =>
    get<LoadAllResponse>({ action: "loadAll", userName }),

  addEntry: (entry: Entry) =>
    post<MutationResponse>({ action: "addEntry", entry }),

  addEntries: (entries: Entry[]) =>
    post<MutationResponse & { count?: number | undefined }>({ action: "addEntries", entries }),

  updateEntry: (entry: Entry) =>
    post<MutationResponse>({ action: "updateEntry", entry }),

  deleteEntry: (entryId: string) =>
    post<MutationResponse>({ action: "deleteEntry", entryId }),

  saveClients: (clients: Client[]) =>
    post<MutationResponse>({ action: "saveClients", clients }),

  saveUserSettings: (userName: string, settings: Partial<Config>) =>
    post<MutationResponse>({ action: "saveUserSettings", userName, settings }),

  // Kept because it maps to a real backend action, but nothing in the UI
  // calls it and nothing should without changing the backend first: the
  // handler clears the whole Config sheet and writes back only the keys it
  // was given. Per-user business info goes through saveUserSettings.
  saveConfig: (config: Partial<Config>) =>
    post<MutationResponse>({ action: "saveConfig", config }),
};
